import { CloseOutlined } from '@ant-design/icons';
import { Dropdown, Empty, Flex, Typography } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { ModelMessageTemplateLite } from '@app/api/MessageTemplates';
import { PREFIX } from '@app/constants';
import { useMessageTemplateOffers } from '@app/hooks';

import type { FormInstance } from 'antd/lib';
import type { FC } from 'react';

import '@app/components/MessageTemplatesWrapper/styles/MessageTemplatesWrapper.desktop.scss';

interface MessageTemplatesWrapperProps {
  children: JSX.Element;
  withTemplateOffers: boolean;
  form?: FormInstance;
  formItemName?: string;
  customMessageValue?: string;
  runReactFn?: (value: string) => void;
  onTemplateSelect?: (template: ModelMessageTemplateLite) => void;
}

const normalize = (value?: string | null) => (value ?? '').trim().toLowerCase();

export const MessageTemplatesWrapper: FC<MessageTemplatesWrapperProps> = ({
  children,
  withTemplateOffers,
  form,
  formItemName,
  customMessageValue,
  runReactFn,
  onTemplateSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hiddenOfferIds, setHiddenOfferIds] = useState<string[]>([]);

  const isInteractingWithDropdownRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const watchedMessageValue =
    (form && formItemName
      ? (form.getFieldValue(formItemName) as string | undefined)
      : undefined) ?? '';

  const message = runReactFn ? customMessageValue : watchedMessageValue;

  const adaptMessageToServer = useCallback((msg?: string) => {
    const handled = (msg || '').trim();
    if (!handled) return '';

    const slashIndex = handled.indexOf('/');
    if (slashIndex !== -1) {
      return handled
        .slice(slashIndex + 1)
        .trim()
        .toLowerCase();
    }

    return handled.toLowerCase();
  }, []);

  const { offers } = useMessageTemplateOffers({
    query: adaptMessageToServer(message),
    withTemplateOffers,
  });

  const visibleOffers = useMemo(() => {
    return offers.filter(({ Id }) => !hiddenOfferIds.includes(String(Id)));
  }, [offers, hiddenOfferIds]);

  const applyTemplate = useCallback(
    (template: ModelMessageTemplateLite) => {
      const templateMessage = template.Message ?? '';

      if (form && formItemName) {
        form.setFieldValue(formItemName, templateMessage);
      }

      if (runReactFn) {
        runReactFn(templateMessage);
      }

      onTemplateSelect?.(template);
    },
    [form, formItemName, onTemplateSelect, runReactFn]
  );

  const handleFocusCapture = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleBlurCapture = useCallback(() => {
    if (isInteractingWithDropdownRef.current) return;
    setIsOpen(false);
  }, []);

  const dropdownContent = useMemo(() => {
    if (!visibleOffers.length) {
      return (
        <div className="message-templates-dropdown">
          <Empty description="Нет подходящих шаблонов" />
        </div>
      );
    }

    return (
      <div
        className="message-templates-dropdown"
        onMouseDown={(e) => {
          isInteractingWithDropdownRef.current = true;
          e.preventDefault();
        }}
        onMouseUp={() => {
          setTimeout(() => {
            isInteractingWithDropdownRef.current = false;
          }, 0);
        }}
      >
        {visibleOffers.map((template) => {
          const optionId = String(template.Id);

          return (
            <div
              key={optionId}
              className="message-templates-option"
              onMouseDown={(e) => {
                e.preventDefault();

                const handledTemplateMessage = normalize(template.Message);
                if (!handledTemplateMessage) return;

                applyTemplate(template);
                setIsOpen(false);
              }}
            >
              <Flex justify="space-between">
                <Flex gap="8px" align="center" className="message-container">
                  <Typography.Text strong className="template-text">
                    {template.Name}:
                  </Typography.Text>
                  <span className="message-text">{template.Message}</span>
                </Flex>

                <CloseOutlined
                  className="close-icon"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setHiddenOfferIds((prev) => [...prev, optionId]);
                  }}
                />
              </Flex>
            </div>
          );
        })}
      </div>
    );
  }, [applyTemplate, visibleOffers]);

  if (!withTemplateOffers) {
    return children;
  }

  const shouldShowDropdown = isOpen && visibleOffers.length > 0;

  return (
    <Dropdown
      open={shouldShowDropdown}
      onOpenChange={setIsOpen}
      placement="bottomLeft"
      popupRender={() => dropdownContent}
      getPopupContainer={() => containerRef.current ?? document.body}
      overlayClassName={`${PREFIX}message-templates-dropdown-overlay`}
      trigger={['click']}
    >
      <div
        ref={containerRef}
        className={`${PREFIX}message-templates-wrapper`}
        onFocusCapture={handleFocusCapture}
        onBlurCapture={handleBlurCapture}
      >
        {children}
      </div>
    </Dropdown>
  );
};
